import { useState, useEffect } from "react";
import { Bell, Slack, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const eventCategories = [
  {
    category: "Demos",
    events: [
      { key: "demo_booked", label: "New demo booked" },
      { key: "demo_completed", label: "Demo completed" },
    ],
  },
  {
    category: "Billing",
    events: [
      { key: "payment_succeeded", label: "Payment succeeded" },
      { key: "payment_failed", label: "Payment failed" },
    ],
  },
  {
    category: "Support",
    events: [
      { key: "support_ticket_update", label: "Ticket updates" },
    ],
  },
];

interface Preference {
  event_type: string;
  slack_enabled: boolean;
  email_enabled: boolean;
}

export default function NotificationsSettings() {
  const [preferences, setPreferences] = useState<Record<string, { slack_enabled: boolean; email_enabled: boolean }>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // Build default preferences (opt-in by default)
  const buildDefaults = () => {
    const defaults: Record<string, { slack_enabled: boolean; email_enabled: boolean }> = {};
    for (const cat of eventCategories) {
      for (const evt of cat.events) {
        defaults[evt.key] = { slack_enabled: true, email_enabled: true };
      }
    }
    return defaults;
  };

  useEffect(() => {
    const fetchPreferences = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      setUserId(user.id);

      const { data, error } = await supabase
        .from("notification_preferences")
        .select("event_type, slack_enabled, email_enabled")
        .eq("user_id", user.id);

      if (error) {
        console.error("Failed to fetch notification preferences:", error);
        toast.error("Failed to load notification preferences");
        setPreferences(buildDefaults());
      } else {
        const defaults = buildDefaults();
        // Override defaults with saved preferences
        if (data) {
          for (const pref of data as Preference[]) {
            defaults[pref.event_type] = {
              slack_enabled: pref.slack_enabled,
              email_enabled: pref.email_enabled,
            };
          }
        }
        setPreferences(defaults);
      }

      setLoading(false);
    };

    fetchPreferences();
  }, []);

  const togglePref = (eventKey: string, channel: "slack_enabled" | "email_enabled") => {
    setPreferences((prev) => ({
      ...prev,
      [eventKey]: {
        ...prev[eventKey],
        [channel]: !prev[eventKey]?.[channel],
      },
    }));
  };

  const handleSave = async () => {
    if (!userId) return;

    setSaving(true);
    try {
      const rows = Object.entries(preferences).map(([event_type, prefs]) => ({
        user_id: userId,
        event_type,
        slack_enabled: prefs.slack_enabled,
        email_enabled: prefs.email_enabled,
      }));

      const { error } = await supabase
        .from("notification_preferences")
        .upsert(rows, { onConflict: "user_id,event_type" });

      if (error) throw error;
      toast.success("Notification preferences saved");
    } catch (error: any) {
      toast.error(error.message || "Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl">
        <VentureCard>
          <VentureCardHeader>
            <VentureCardTitle>Notification Preferences</VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent>
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded" />
              <div className="h-8 bg-muted rounded" />
              <div className="h-8 bg-muted rounded" />
              <div className="h-8 bg-muted rounded" />
              <div className="h-8 bg-muted rounded" />
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notification Preferences
          </VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent className="space-y-4">
          {/* Column headers */}
          <div className="flex items-center justify-end gap-8 text-xs font-medium text-muted-foreground pb-2 border-b border-border">
            <div className="flex items-center gap-1">
              <Slack className="w-3 h-3" />
              Slack
            </div>
            <div className="flex items-center gap-1">
              <Mail className="w-3 h-3" />
              Email
            </div>
          </div>

          {/* Event categories */}
          {eventCategories.map((cat) => (
            <div key={cat.category}>
              <p className="font-medium mb-2">{cat.category}</p>
              {cat.events.map((evt) => (
                <div key={evt.key} className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">{evt.label}</span>
                  <div className="flex items-center gap-8">
                    <input
                      type="checkbox"
                      checked={preferences[evt.key]?.slack_enabled ?? true}
                      onChange={() => togglePref(evt.key, "slack_enabled")}
                      className="w-4 h-4 rounded border-border bg-surface accent-secondary"
                    />
                    <input
                      type="checkbox"
                      checked={preferences[evt.key]?.email_enabled ?? true}
                      onChange={() => togglePref(evt.key, "email_enabled")}
                      className="w-4 h-4 rounded border-border bg-surface accent-secondary"
                    />
                  </div>
                </div>
              ))}
            </div>
          ))}

          <Button variant="gradient" className="w-full" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Preferences"}
          </Button>
        </VentureCardContent>
      </VentureCard>
    </div>
  );
}
